// ════════════════════════════════════════════════════════════════════
// LYRAEA — Player.js   ·   the bar and the sheet
// ────────────────────────────────────────────────────────────────────
// Two surfaces over one engine:
//   • THE BAR   — pinned to the bottom, always there once something is
//                 cued. Cover, title, play/pause, a hairline progress
//                 line. Tap anywhere on it to open the sheet.
//   • THE SHEET — the full-screen now-playing view. Big art, scrubber,
//                 shuffle/repeat, queue.
//
// Neither owns any audio state — both read useAudio() and call the
// engine's functions. That is why the bar can live under one screen and
// the sheet over another with no coordination between them.
//
// The bar's height is --bar-h in index.css, read by both the bar and the
// scroll padding of every screen, so a taller bar can never start hiding
// the last row of a list.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import {
  useAudio, toggle, next, prev, seek, jumpTo,
  toggleShuffle, cycleRepeat, setVolume, setSleepTimer,
  removeAt, moveInQueue,
} from "./audio";
import { resolveCover } from "./CDN";
import { fmtTime, useExtras, extrasFor } from "./lyraeaData";
import { ExtrasPanel, TextBody } from "./Extras";
import Sleeve, { sleeveHue } from "./Sleeve";
import { BookControls, FollowText, ChapterList } from "./BookView";
import Visualizer, { VisPicker, useVis, useVisShow, setVisShow } from "./Visualizer";

const C = {
  void: "var(--void)", obsidian: "var(--obsidian)", slab: "var(--slab)",
  slabHi: "var(--slab-hi)", edge: "var(--edge)", edgeHot: "var(--edge-hot)",
  gold: "var(--gold)", goldHi: "var(--gold-hi)", violet: "var(--violet)",
  bone: "var(--bone)", ash: "var(--ash)", smoke: "var(--smoke)", verd: "var(--verd)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

// ── Cover art with a graceful nothing-state ──────────────────────────
// A cover is either real art or a generated sleeve — never an empty box.
// See Sleeve.js for why: most tracks will never have art, and a wall of
// grey squares is what makes a library look unfinished.
export function Cover({ src, size = 46, radius = 0, glow, seed, title }) {
  const url = resolveCover(src);
  const [broken, setBroken] = useState(false);

  // The generated sleeve is ALWAYS the base layer, with real art painted
  // over it. Two reasons, and the second one is the important one:
  //
  //   1. No flash of empty box while a cover downloads.
  //   2. A CSS `background-image` CANNOT REPORT FAILURE. A typo'd filename,
  //      a missing extension, a moved asset, a CDN blip — all render as a
  //      silent black square with no way for the app to know. An <img> has
  //      an onError, so a broken cover degrades to its generated sleeve
  //      instead of a hole in the shelf.
  //
  // `broken` resets on src change so fixing a filename recovers without a
  // reload.
  const showArt = url && !broken;

  return (
    <div style={{
      position: "relative", width: size, height: size, flex: "0 0 auto",
      borderRadius: radius, lineHeight: 0,
      boxShadow: glow ? `0 14px 50px #000a, 0 0 44px ${C.violet}22` : "none",
    }}>
      <Sleeve seed={seed || title || src} title={title} size="100%" />
      {url && (
        <img
          key={url}
          src={url}
          alt=""
          onError={() => setBroken(true)}
          onLoad={() => setBroken(false)}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", opacity: showArt ? 1 : 0,
            transition: "opacity .3s",
            // The same bevel the generated sleeves carry, so real art and
            // generated art sit on one shelf without either looking foreign.
            clipPath: "polygon(0 0, 86% 0, 100% 14%, 100% 100%, 14% 100%, 0 86%)",
          }}
        />
      )}
    </div>
  );
}

// ── Three dancing bars — the "this one is playing" tell ──────────────
export function Equalizer({ color = "var(--verd)", size = 12 }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 1.5, height: size, width: size }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{
          flex: 1, background: color, borderRadius: 1,
          animation: `lyBar${i} ${0.7 + i * 0.15}s ease-in-out infinite`,
        }} />
      ))}
    </div>
  );
}

// ── An icon button, one style, everywhere ────────────────────────────
const IconBtn = ({ children, onClick, size = 20, tint = C.bone, title, active }) => (
  <button onClick={(e) => { e.stopPropagation(); onClick?.(); }} title={title} style={{
    background: "none", border: "none", padding: 6, lineHeight: 1,
    color: active ? C.goldHi : tint, fontSize: size, fontFamily: C.display,
    opacity: active ? 1 : 0.9, transition: "color .2s, opacity .2s",
  }}>{children}</button>
);

// ── The scrubber. Pointer-driven so it works with touch and mouse ────
function Scrubber({ position, duration, tall }) {
  const [drag, setDrag] = useState(null);
  const pct = duration ? ((drag ?? position) / duration) * 100 : 0;

  const posFromEvent = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    return Math.max(0, Math.min(1, x)) * (duration || 0);
  };

  return (
    <div
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag(posFromEvent(e));
      }}
      onPointerMove={(e) => { if (drag !== null) setDrag(posFromEvent(e)); }}
      onPointerUp={(e) => {
        if (drag !== null) { seek(posFromEvent(e)); setDrag(null); }
      }}
      style={{
        position: "relative", height: tall ? 22 : 3, display: "flex",
        alignItems: "center", cursor: "pointer", touchAction: "none",
      }}
    >
      <div style={{ position: "relative", width: "100%", height: 3, background: C.edge }}>
        <div style={{
          position: "absolute", inset: 0, width: `${pct}%`,
          background: `linear-gradient(90deg, ${C.gold}, ${C.goldHi})`,
          boxShadow: `0 0 10px ${C.gold}66`,
        }} />
        {tall && (
          <div style={{
            position: "absolute", left: `${pct}%`, top: "50%",
            width: 11, height: 11, marginLeft: -5.5, marginTop: -5.5,
            borderRadius: "50%", background: C.goldHi,
            boxShadow: `0 0 12px ${C.gold}`,
          }} />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE BAR
// ════════════════════════════════════════════════════════════════════
export function NowBar({ onOpen, onOpenArtist }) {
  const a = useAudio();
  if (!a.track) return null;

  return (
    <div onClick={onOpen} style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 500,
      background: `linear-gradient(180deg, ${C.slab}f2, ${C.void}fa)`,
      borderTop: `1px solid ${C.edge}`,
      backdropFilter: "blur(14px)",
      paddingBottom: "var(--safe-b)",
      cursor: "pointer",
      animation: "lyRise .3s cubic-bezier(.22,.61,.36,1) both",
    }}>
      <Scrubber position={a.position} duration={a.duration} />
      <div style={{
        display: "flex", alignItems: "center", gap: 11,
        padding: "9px 13px", height: "var(--bar-h)",
        width: "100%", maxWidth: 940, margin: "0 auto",
      }}>
        <Cover src={a.track.cover} seed={a.track.id} title={a.track.title} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: C.sans, fontSize: 16, color: C.bone, letterSpacing: ".02em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{a.track.title}</div>
          <div
            onClick={(e) => { e.stopPropagation(); onOpenArtist?.(a.track.artistUid); }}
            style={{
              fontFamily: C.mono, fontSize: 10, color: C.smoke, letterSpacing: ".1em",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              textTransform: "uppercase",
            }}
          >{a.track.book ? (a.track.book.chapters[a.chapter]?.title || "") : (a.track.artist || "UNKNOWN")}</div>
        </div>
        {a.loading
          ? <span style={{ color: C.smoke, fontFamily: C.mono, fontSize: 10, animation: "lyPulse 1.2s infinite" }}>…</span>
          : <Equalizer color={a.playing ? C.verd : C.smoke} />}
        <IconBtn onClick={toggle} size={22} title={a.playing ? "Pause" : "Play"}>
          {a.playing ? "❚❚" : "▶"}
        </IconBtn>
        {!a.track.book && <IconBtn onClick={() => next("user")} size={18} title="Next">▶▶</IconBtn>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE SHEET
// ════════════════════════════════════════════════════════════════════
// A song's lyrics on the stage — same frame, same SIDE/OVERLAY chip as a
// book's FollowText, minus the karaoke (song lyrics carry no timing).
function SongWords({ lyrics, mode, onMode }) {
  const chip = (on) => ({
    background: "none", border: `1px solid ${on ? C.gold : C.edge}`, color: on ? C.goldHi : C.smoke,
    fontFamily: C.mono, fontSize: 9, letterSpacing: ".16em", padding: "5px 8px", flex: "0 0 auto",
  });
  return (
    <div className="ly-words-frame" style={{ position: "relative" }}>
      <div className="ly-words-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, margin: "2px 2px 10px" }}>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: ".2em", color: C.gold, textTransform: "uppercase",
          minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lyrics.title || "Lyrics"}</div>
        <button onClick={() => onMode(mode === "over" ? "side" : "over")} style={chip(mode === "over")}
          title="Side by side with the cover, or over a faded cover">
          {mode === "over" ? "OVERLAY" : "SIDE"}
        </button>
      </div>
      <div className="ly-words-scroll ly-scroll">
        <div className="ly-words" style={{ paddingTop: "6vh", paddingBottom: "18vh", textAlign: mode === "over" ? "center" : "left" }}>
          <TextBody body={lyrics.body} />
        </div>
      </div>
    </div>
  );
}

export function NowSheet({ onClose, onOpenArtist }) {
  const a = useAudio();
  // Three views, not a boolean. It was `showQueue` when there were two;
  // adding EXTRAS as a second boolean is how you end up with a state
  // where both are true and the sheet renders two panels on top of each
  // other. One name, three values, impossible to be in two at once.
  // An audiobook adds "chapters" (phones only; wide screens have the column).
  const [view, setView] = useState("art");   // "art" | "queue" | "extras" | "chapters"
  // The words are NOT a separate screen — they live on the art view, beside
  // the cover ("side") or over a ghosted cover ("over"). Remembered.
  const [lyr, setLyrRaw] = useState(() => {
    try { return localStorage.getItem("lyraea.lyrics") || "off"; } catch { return "off"; }
  });
  const setLyr = (v) => { setLyrRaw(v); try { localStorage.setItem("lyraea.lyrics", v); } catch {} };
  const lastLyr = () => { try { return localStorage.getItem("lyraea.lyrics.mode") || "side"; } catch { return "side"; } };
  const allExtras = useExtras();
  // The chosen Grace, and whether the stage shows it instead of the cover.
  const vis = useVis();
  const visOn = useVisShow();
  // The chapters column folds away (remembered); FULL takes the whole screen.
  const [chapOpen, setChapOpenRaw] = useState(() => { try { return localStorage.getItem("lyraea.chapters") !== "0"; } catch { return true; } });
  const setChapOpen = (v) => { setChapOpenRaw(v); try { localStorage.setItem("lyraea.chapters", v ? "1" : "0"); } catch {} };
  const [full, setFull] = useState(false);
  useEffect(() => {
    const sync = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener("fullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);
  const toggleFull = () => {
    const on = !full;
    setFull(on);
    // The browser's own fullscreen takes the tab bar and taskbar too. If it is
    // refused (iframe, iOS), the sheet still goes gutter-to-gutter.
    try {
      if (on && !document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
      if (!on && document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    } catch {}
  };
  if (!a.track) return null;

  // A track inherits its album's extras — liner notes belong to the
  // record, and somebody on track four should not have to go and find
  // them. extrasFor() puts the track's own first.
  const mine = extrasFor(allExtras, {
    trackId: a.track.id,
    albumId: a.track.albumId || null,
  });

  // A song's own lyrics (an EXTRA) get the same two ways a book's words do:
  // SIDE, or OVERLAY on a faded cover / Grace.
  const songLyrics = !a.track.book ? mine.find((e) => e.kind === "lyrics" && e.body) : null;
  const hasWords = !!(a.track.book || songLyrics);

  // The room takes its light from the record on the turntable.
  const hue = sleeveHue(a.track.id || a.track.title);

  const repeatGlyph = a.repeat === "one" ? "🅡¹" : "🅡";

  return (
    <div className={[a.track.book && "ly-sheet-book", a.track.book && !chapOpen && "ly-chap-closed", full && "ly-full"].filter(Boolean).join(" ") || undefined} style={{
      position: "fixed", inset: 0, zIndex: 900,
      background: `linear-gradient(178deg, ${C.slab}, ${C.void} 62%)`,
      display: "flex", flexDirection: "column",
      padding: `calc(14px + env(safe-area-inset-top,0px)) 18px calc(18px + var(--safe-b))`,
      animation: "lyRise .28s cubic-bezier(.22,.61,.36,1) both",
      overflow: "hidden",
    }}>
      {/* A book's chapters sit in a column down the left on a wide screen,
          so any chapter is one click away without leaving the controls.
          On a phone there is no room: the CHAPTERS tab does the same job
          (index.css hides one or the other by width). */}
      {a.track.book && (
        <aside className="ly-book-aside ly-scroll">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: C.mono, fontSize: 9, letterSpacing: ".3em", color: C.smoke, padding: "4px 4px 8px" }}>
            <span>CHAPTERS · {a.track.book.chapters.length}</span>
            <button onClick={() => setChapOpen(false)} title="Fold the chapters away" style={{
              background: "none", border: "none", color: C.ash, fontFamily: C.mono, fontSize: 11, padding: "0 4px", cursor: "pointer",
            }}>«</button>
          </div>
          <ChapterList a={a} compact />
        </aside>
      )}

      {/* the cover's own colour, bloomed behind everything */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", opacity: .5,
        background: `radial-gradient(ellipse at 50% 22%, ${hue.dark}3d, transparent 62%)`,
      }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: C.ash,
          fontFamily: C.mono, fontSize: 11, letterSpacing: ".26em", padding: 4,
        }}>▾ CLOSE</button>
        <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".3em", color: C.smoke }}>
          NOW PLAYING
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          {/* The EXTRAS tab is only offered when there is something behind
              it. An always-present tab that is usually empty teaches the
              listener to stop pressing it, which is how a feature dies
              while still being shipped. It carries its own count so the
              tap is never a gamble. */}
          {mine.length > 0 && (
            <button onClick={() => setView((v) => (v === "extras" ? "art" : "extras"))} style={{
              background: "none", border: "none", color: view === "extras" ? C.goldHi : C.ash,
              fontFamily: C.mono, fontSize: 11, letterSpacing: ".22em", padding: 4,
            }}>EXTRAS·{mine.length}</button>
          )}
          {hasWords && (
            <button onClick={() => { setView("art"); setLyr(lyr === "off" || view !== "art" ? lastLyr() : "off"); }} style={{
              background: "none", border: "none", color: lyr !== "off" && view === "art" ? C.goldHi : C.ash,
              fontFamily: C.mono, fontSize: 11, letterSpacing: ".22em", padding: 4,
            }}>{a.track.book ? "READ" : "LYRICS"}</button>
          )}
          {a.track.book && (
            <button className={chapOpen && !full ? "ly-narrow-only" : undefined} onClick={() => {
              // Wide screen: the column folds back out. Phone: the chapters view.
              const wide = window.matchMedia?.("(min-width: 900px)").matches;
              if (wide && !full) { setChapOpen(true); return; }
              setView((cur) => (cur === "chapters" ? "art" : "chapters"));
            }} style={{
              background: "none", border: "none", color: view === "chapters" ? C.goldHi : C.ash,
              fontFamily: C.mono, fontSize: 11, letterSpacing: ".22em", padding: 4,
            }}>CHAPTERS</button>
          )}
          <button onClick={toggleFull} title="True full screen" style={{
            background: "none", border: "none", color: full ? C.goldHi : C.ash,
            fontFamily: C.mono, fontSize: 11, letterSpacing: ".22em", padding: 4,
          }}>{full ? "⤡ EXIT" : "⤢ FULL"}</button>
          <button onClick={() => setView((v) => (v === "vis" ? "art" : "vis"))} style={{
            background: "none", border: "none", color: view === "vis" || visOn ? C.goldHi : C.ash,
            fontFamily: C.mono, fontSize: 11, letterSpacing: ".22em", padding: 4,
          }}>VIS</button>
          <button onClick={() => setView((v) => (v === "queue" ? "art" : "queue"))} style={{
            background: "none", border: "none", color: view === "queue" ? C.goldHi : C.ash,
            fontFamily: C.mono, fontSize: 11, letterSpacing: ".26em", padding: 4,
          }}>QUEUE</button>
        </div>
      </div>

      {a.track.book && view === "chapters" ? (
        <div className="ly-scroll" style={{ flex: 1, overflowY: "auto", position: "relative", maxWidth: 680, width: "100%", margin: "14px auto 0" }}>
          <ChapterList a={a} onPicked={() => setView("art")} />
        </div>
      ) : view === "vis" ? (
        <div className="ly-scroll" style={{ flex: 1, overflowY: "auto", position: "relative", maxWidth: 680, width: "100%", margin: "14px auto 0" }}>
          <VisPicker C={C} onPicked={() => setView("art")} />
        </div>
      ) : view === "extras" ? (
        <div className="ly-scroll" style={{ flex: 1, overflowY: "auto", marginTop: 14, position: "relative", maxWidth: 620, width: "100%", margin: "14px auto 0" }}>
          <ExtrasPanel extras={mine} />
        </div>
      ) : view === "art" ? (
        <>
          {/* THE STAGE — the cover, and for a book the words with it. Layout
              per mode lives in index.css (.ly-stage-*), so the switch between
              cover-only, side by side and overlay is a class, and animates. */}
          {(() => { const mode = hasWords ? lyr : "off"; return (
          <div className={`ly-stage ly-stage-${mode}`}>
            <div className={`ly-stage-cover${visOn ? " ly-vis-on" : ""}`} style={{ position: "relative" }}>
              {visOn
                ? <Visualizer visId={vis} seed={a.track.id || a.track.title} playing={a.playing}
                    style={{ aspectRatio: full ? "auto" : "1", boxShadow: "0 18px 60px #000c" }} />
                : <Cover src={a.track.cover} seed={a.track.id} title={a.track.title} size="100%" glow />}
              {/* one tap between the album and the Grace — nothing else changes */}
              {vis !== "off" && (
                <button className="ly-swap" onClick={() => setVisShow(!visOn)}>
                  {visOn ? "◐ COVER" : "◐ VISUAL"}
                </button>
              )}
            </div>
            {mode !== "off" && (
              <div className="ly-stage-words">
                {a.track.book
                  ? <FollowText a={a} compact mode={mode}
                      onMode={(m) => { setLyr(m); try { localStorage.setItem("lyraea.lyrics.mode", m); } catch {} }} />
                  : <SongWords lyrics={songLyrics} mode={mode}
                      onMode={(m) => { setLyr(m); try { localStorage.setItem("lyraea.lyrics.mode", m); } catch {} }} />}
              </div>
            )}
          </div>
          ); })()}

          <div className="ly-full-wide" style={{ position: "relative", width: "100%", maxWidth: 620, margin: "0 auto" }}>
            <div style={{
              fontFamily: C.display, fontWeight: 800, fontSize: 27, color: C.bone,
              lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{a.track.title}</div>
            <div
              onClick={() => onOpenArtist?.(a.track.artistUid)}
              style={{
                fontFamily: C.display, fontStyle: "italic", fontSize: 16,
                color: C.gold, marginTop: 2, cursor: onOpenArtist ? "pointer" : "default",
              }}
            >{a.track.artist || "Unknown"}</div>
            {a.track.book && (
              <div onClick={() => setView("chapters")} style={{
                fontFamily: C.mono, fontSize: 10, letterSpacing: ".14em", color: C.ash,
                marginTop: 6, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {String(a.chapter + 1).padStart(3, "0")}/{a.track.book.chapters.length} · {a.track.book.chapters[a.chapter]?.title}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <Scrubber position={a.position} duration={a.duration} tall />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke }}>{fmtTime(a.position)}</span>
                <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke }}>{fmtTime(a.duration)}</span>
              </div>
            </div>

            {a.track.book ? <BookControls a={a} /> : (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 12, gap: 6,
            }}>
              <IconBtn onClick={toggleShuffle} size={16} active={a.shuffle} title="Shuffle" tint={C.smoke}>⤨</IconBtn>
              <IconBtn onClick={prev} size={24} title="Previous">◀◀</IconBtn>
              <button onClick={toggle} style={{
                width: 62, height: 62, borderRadius: "50%",
                background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`,
                border: "none", color: C.void, fontSize: 22, fontFamily: C.display,
                boxShadow: `0 8px 30px ${C.gold}55`,
              }}>{a.playing ? "❚❚" : "▶"}</button>
              <IconBtn onClick={() => next("user")} size={24} title="Next">▶▶</IconBtn>
              <IconBtn onClick={cycleRepeat} size={15} active={a.repeat !== "off"} title={`Repeat: ${a.repeat}`} tint={C.smoke}>
                {repeatGlyph}
              </IconBtn>
            </div>
            )}

            {a.error && (
              <div style={{
                marginTop: 12, fontFamily: C.sans, fontSize: 13, color: C.bone,
                background: "#c0142722", border: `1px solid ${C.edge}`, padding: "8px 10px",
              }}>{a.error}</div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 14, opacity: .8 }}>
              <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke }}>VOL</span>
              <input
                type="range" min={0} max={1} step={0.01} value={a.volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                style={{ flex: 1, accentColor: "#d4a03c" }}
              />
            </div>

            {/* Sleep timer — the reason anyone listens lying down. */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 12, flexWrap: "wrap" }}>
              <span style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".2em", color: C.smoke }}>
                SLEEP
              </span>
              {[15, 30, 60].map((min) => {
                const on = a.sleepAt && Math.abs(a.sleepAt - Date.now() - min * 60000) < 90000;
                return (
                  <button key={min} onClick={() => setSleepTimer(on ? null : min)} style={{
                    background: "none", border: `1px solid ${on ? C.gold : C.edge}`,
                    color: on ? C.goldHi : C.smoke, fontFamily: C.mono, fontSize: 9,
                    letterSpacing: ".14em", padding: "5px 9px",
                  }}>{min}M</button>
                );
              })}
              {a.sleepAt && (
                <button onClick={() => setSleepTimer(null)} style={{
                  background: "none", border: `1px solid ${C.edge}`, color: C.ash,
                  fontFamily: C.mono, fontSize: 9, letterSpacing: ".14em", padding: "5px 9px",
                }}>
                  OFF · {Math.max(0, Math.round((a.sleepAt - Date.now()) / 60000))}M LEFT
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="ly-scroll" style={{ flex: 1, overflowY: "auto", marginTop: 14, position: "relative" }}>
          {/* ⚠ REORDER AND REMOVE ARE BUTTONS, NOT DRAG. Drag-to-reorder
              inside a scrolling list on a touch screen is a coin toss
              between "I moved the row" and "I scrolled the list", and a
              reorder that silently does the wrong thing is worse than
              two arrows. The engine's moveInQueue/removeAt never touch
              the audio element, so editing the queue cannot interrupt
              the song that is playing — including editing the row that
              IS playing. */}
          {a.queue.map((t, i) => (
            <div key={`${t.id}-${i}`}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "9px 4px",
                borderBottom: `1px solid ${C.edge}`,
                opacity: i === a.index ? 1 : 0.72,
              }}>
              <span onClick={() => jumpTo(i)} style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke, width: 22, cursor: "pointer" }}>
                {i === a.index ? "◆" : String(i + 1).padStart(2, "0")}
              </span>
              <div onClick={() => jumpTo(i)} style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, cursor: "pointer" }}>
                <Cover src={t.cover} seed={t.id} title={t.title} size={34} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: C.sans, fontSize: 15, color: i === a.index ? C.goldHi : C.bone,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{t.title}</div>
                  <div style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".1em", textTransform: "uppercase" }}>
                    {t.artist}
                  </div>
                </div>
                <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke }}>{fmtTime(t.durationSec)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
                <IconBtn onClick={() => moveInQueue(i, i - 1)} size={12} tint={i === 0 ? C.edge : C.smoke} title="Up">▲</IconBtn>
                <IconBtn onClick={() => moveInQueue(i, i + 1)} size={12} tint={i === a.queue.length - 1 ? C.edge : C.smoke} title="Down">▼</IconBtn>
                <IconBtn onClick={() => removeAt(i)} size={13} tint={C.smoke} title="Remove from queue">✕</IconBtn>
              </div>
            </div>
          ))}
          {!a.queue.length && (
            <div style={{
              fontFamily: C.mono, fontSize: 10, letterSpacing: ".18em", color: C.smoke,
              textAlign: "center", padding: "34px 10px",
            }}>THE QUEUE IS EMPTY</div>
          )}
        </div>
      )}
    </div>
  );
}

export default NowBar;
